<template>
    <div class="share-certificate-split">
        <b-sidebar
            id="sidebar-task-handler"
            data-test="sidebar"
            sidebar-class="sidebar-lg"
            :visible="isSplitShareCertificateSidebarActive"
            bg-variant="white"
            shadow
            backdrop
            no-header
            right
            @change="
                (val) => $emit('update:is-split-share-certificate-sidebar-active', val)
            "
            @hidden="clearFormData"
        >
            <template #default="{ hide }">
                <!-- Header -->
                <div
                    class="
                        d-flex
                        justify-content-between
                        align-items-center
                        content-sidebar-header
                        px-2
                        py-1
                        side-bar-header
                    "
                >
                    <h3 class="mb-0">
                        {{ sideBarTitle }}
                    </h3>

                    <div>
                        <feather-icon
                            class="ml-1 cursor-pointer"
                            icon="XIcon"
                            size="16"
                            @click="hide"
                        />
                    </div>
                </div>

                <!-- Body -->
                <div v-show="! showCreateOrUpdateShareCertificateForm">
                    <validation-observer
                        v-slot="{ handleSubmit }"
                        ref="refFormObserver"
                    >
                        <!-- Form -->
                        <b-form
                            class="p-2"
                            @submit.prevent="handleSubmit(onSubmit)"
                            @reset.prevent="resetForm"
                        >
                            <!-- Title -->
                            <validation-provider
                                v-slot="{ errors }"
                                name="shareholder"
                                rules="required"
                            >
                                <b-form-group label="Shareholder" :state="errors.length > 0 ? false : null">
                                    <v-select
                                        id="shareholder"
                                        ref="shareholder"
                                        v-model="shareCertificateLocal.shareholder"
                                        class="custom-select-icon"
                                        :clearable="false"
                                        placeholder="Select shareholder"
                                        label="name"
                                        disabled
                                        :options="shareholderOptions"
                                    >
                                        <div slot="no-options" class="d-flex justify-content-center">
                                            <normal-loader v-if="shareholderOptions.length <= 0" />
                                            <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                        </div>
                                    </v-select>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
                                v-slot="validationContext"
                                name="number of shares"
                                rules="required"
                            >
                                <b-form-group label="Number of shares" label-for="number-of-shares">
                                    <b-form-input
                                        id="number-of-shares"
                                        v-model="shareCertificateLocal.numberOfShares"
                                        autofocus
                                        :state="
                                            getValidationState(validationContext)
                                        "
                                        disabled
                                        trim
                                        placeholder="Enter number of shares"
                                        type="text"
                                        data-test ="name"
                                        no-wheel
                                        @keydown.up.prevent
                                        @keydown.down.prevent
                                    />

                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>


                            <div class="manage certificates mb-1 mt-2">
                                <div class="d-flex justify-content-between">
                                    <div style="position: relative; top: 5px;">
                                        <label> Certificates </label>
                                    </div>
                                    
                                    <b-button
                                        v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                                        class="btn btn-sm d-flex justify-content-end align-items-center"
                                        style="margin-bottom:5px"
                                        variant="outline-primary"
                                        @click="addNewCertificate"
                                    >
                                        New

                                        <feather-icon 
                                            class="ml-.5 d-flex justify-content-center align-items-center" 
                                            width="14px" 
                                            height="14px" 
                                            icon="PlusIcon" 
                                        />
                                    </b-button>
                                </div>
                            
                                <table v-if="splitCertificatesData.length > 0" class="table-bordered w-100">
                                    <th class="p-1"> Certificate No. </th>
                                    <th class="p-1"> No. of Shares </th>
                                    <th class="p-1"> Action </th>

                                    <tr v-for="certificate in splitCertificatesData" :key="certificate._id">
                                        <td class="p-1"> 
                                           {{ certificate.number }}
                                        </td>

                                        <td class="p-1"> 
                                            {{ certificate.numberOfShares }}
                                        </td>

                                        <td class="p-1">
                                            <div class="d-flex justify-content-center align-items-center">
                                                <div class="edit-icon">
                                                    <feather-icon 
                                                        icon="EditIcon" 
                                                        class="cursor-pointer"
                                                        @click="editSplitCertificate(certificate)"
                                                    />
                                                </div>

                                                <div class="cancel-icon ml-.5">
                                                    <feather-icon 
                                                        icon="XCircleIcon" 
                                                        class="cursor-pointer"
                                                        @click="removeFromSplitList(certificate)"
                                                    />
                                                </div> 
                                            </div>
                                        </td>
                                    </tr>
                                </table>

                                <div v-if="showNumberOfShareNotMatchingError && splitCertificatesData.length >= 1" class="text-danger" role="alert">
                                    Total number of shares must be {{ shareCertificateLocal.numberOfShares }}.
                                </div>
                            </div>

                            <!-- Form Actions -->
                            <div class="d-flex">
                                <b-button
                                    v-if="! isDisabled"
                                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                    :disabled="dataSidebarActionLoading"
                                    variant="primary"
                                    class="
                                        mr-1
                                        mt-1
                                        d-flex
                                        justify-content-center
                                        align-items-center
                                    "
                                    type="submit"
                                    data-test="create-share-certificate-btn"
                                    >
                                    <span :class="dataSidebarActionLoading ? 'mr-1' : ''">
                                        {{ dataStatus === 'edit' ? "Update" : " " }}
                                    </span>

                                    <img
                                        v-if="dataStatus === 'create' && !dataSidebarActionLoading"
                                        :src="'/illustrations/add-circle.svg'"
                                        class="ml-1"
                                        alt="add-logo"
                                    />

                                    <img
                                        v-if="dataStatus === 'edit' && !dataSidebarActionLoading"
                                        :src="'/illustrations/edit.svg'"
                                        class="ml-1"
                                        alt="edit-logo"
                                    />
                                    <normal-loader v-if="dataSidebarActionLoading" class="action-button left-10%" />
                                </b-button>

                                <b-button
                                    v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                                    class="
                                    mt-1 
                                    d-flex
                                    justify-content-center
                                    align-items-center"
                                    variant="outline-primary"
                                    data-test="cancel"
                                    @click="$emit('update:is-split-share-certificate-sidebar-active', false)"
                                    >
                                    Cancel
                                    
                                    <img
                                        :src="'/illustrations/cancel-circle.svg'"
                                        class="float-right ml-1"
                                        alt="cancel-logo"
                                    />
                                </b-button>
                            </div>
                        </b-form>
                    </validation-observer>
                </div>

                <transition name="zoom-fade" mode="out-in">
                    <!-- create or view new certificate -->
                    <split
                        v-if="showCreateOrUpdateShareCertificateForm"
                        :data-certificate="dataToViewOrEdit"
                        :new-certificate-status="newCertificateStatus"
                        @cancel="showCreateOrUpdateShareCertificateForm = false"
                        @createOrUpdate="createOrUpdateNewCertificate"
                    />
                </transition>
            </template>
        </b-sidebar>
    </div>
</template>

<script>
import {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BFormInvalidFeedback,
} from "bootstrap-vue";
// import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email } from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import { toRefs, ref, watch } from "@vue/composition-api";
import { NO_MATCHING_OPTIONS } from "@/constants/errors";
import { generateRandomUniqueId, shareMask, unMask } from "@/helpers";
import { errorToast } from "@/views/helpers/toast";
import split from "./Split.vue";

export default {
    components: {
        // BSV
        BForm,
        vSelect,
        BButton,
        BSidebar,
        // flatPickr,
        BFormGroup,
        BFormInput,
        BFormInvalidFeedback,

        // Form Validation
        ValidationProvider,
        ValidationObserver,

        split,
    },

    directives: {
        Ripple,
    },

    model: {
        prop: "isSplitShareCertificateSidebarActive",
        event: "update:is-split-share-certificate-sidebar-active",
    },

    props: {
        isSplitShareCertificateSidebarActive: {
            type: Boolean,
            required: true,
        },

        dataSidebarActionLoading: {
            type: Boolean,
            default: false,
        },

        dataShareCertificate: {
            type: Object,
            required: true,
        },
        
        dataStatus: { 
            type: String,
            default: "view"
        },
    },

    data() {
        return {
            required,
            email,
            shareMask,
            unMask,

            datePickerConfig: {
                altFormat: "j M Y",
                dateFormat: "j M Y",
                disableMobile: "true"
            },

            statusOptions : [],
            shareTypeOptions: [],
            shareholderOptions: [],
            natureOptions: [],
            currencyCode: this.shareCertificateLocal.currency?.currency_code,

            dataToViewOrEdit: {},
            splitCertificatesData: [],
            newCertificateStatus: 'view-new-certificate',
            showCreateOrUpdateShareCertificateForm: false,

            NO_MATCHING_OPTIONS,
        };
    },

    computed: {
        isDisabled() {
            if(this.dataStatus === "view")
                return true;
            return false;
        },

        sideBarTitle() {
            if(this.dataStatus === "view")
                return "Share Certificate Details";

            return "Update Share Certificate";
        },

        dynamicStyle() {
            if(this.dataStatus === "view")
                return "background: #efefef; cursor: context-menu!important";
            return "background: white;";
        },

        showNumberOfShareNotMatchingError() {
            const totalSharesOfCertificate = unMask(this.dataShareCertificate.numberOfShares);

            const totalSharesRegistered = this.splitCertificatesData.reduce((sum, certificate) => unMask(sum) + unMask(certificate.numberOfShares), 0);

            return totalSharesOfCertificate !== totalSharesRegistered;
        }
    },

    watch: {
        isSplitShareCertificateSidebarActive(active) {
            if (active) {
                this.splitCertificatesData = [];

                this.splitCertificatesData.push(
                    {
                        date: this.dataShareCertificate.date,
                        number: this.dataShareCertificate.number,
                        numberOfShares: this.dataShareCertificate.numberOfShares,
                        paidUpShareCapital: this.dataShareCertificate.paidAmount,
                    }
                );
            }
        }
    },

    methods: {
        addNewCertificate() {
            this.newCertificateStatus="create-new-certificate";
            this.showCreateOrUpdateShareCertificateForm = true;
        },

        editSplitCertificate(data) {
            this.dataToViewOrEdit = data;

            this.newCertificateStatus="edit-new-certificate";
            this.showCreateOrUpdateShareCertificateForm = true;
        },

        /* eslint-disable no-param-reassign */
        createOrUpdateNewCertificate (data) {
            if(this.newCertificateStatus === 'edit-new-certificate') {
                this.splitCertificatesData.forEach(certificate => {
                    if(certificate._id === data._id) {
                        certificate.date = data.date;
                        certificate.number = data.number;
                        certificate.numberOfShares = data.numberOfShares;
                        certificate.paidUpShareCapital = data.paidUpShareCapital;
                    }
                });
                
            } else {
                // eslint-disable-next-line no-param-reassign
                data._id = generateRandomUniqueId();
                this.splitCertificatesData.push(data);  
            }

            this.shareCertificateLocal.splitCertificates = this.splitCertificatesData;
            this.showCreateOrUpdateShareCertificateForm = false;
            this.dataToViewOrEdit = {};
        },
        /* eslint-enable no-param-reassign */

        removeFromSplitList(removingData) {
            this.splitCertificatesData = this.splitCertificatesData.filter(data => data._id !== removingData._id);
        
            this.shareCertificateLocal.splitCertificates = this.splitCertificatesData;
        },
    },
   
    setup(props, { emit }) {
        // eslint-disable-next-line no-shadow
        function useShareCertificateHandler(props) {
            const shareCertificateLocal = ref(JSON.parse(JSON.stringify(props.dataShareCertificate.value)));
            
            const resetShareCertificateLocal = () => {
                // eslint-disable-next-line no-use-before-define
                clearForm();
                shareCertificateLocal.value = JSON.parse(JSON.stringify(props.dataShareCertificate.value));
            };

            watch(props.dataShareCertificate, () => {
                resetShareCertificateLocal();
            });
           
            const onSubmit = () => {
                const totalSharesOfCertificate = unMask(shareCertificateLocal.value.numberOfShares);

                const totalSharesRegistered = shareCertificateLocal.value.splitCertificates.reduce((sum, certificate) => unMask(sum) + unMask(certificate.numberOfShares), 0);

                if(totalSharesOfCertificate !== totalSharesRegistered) {
                    errorToast(`Total number of shares must be ${totalSharesOfCertificate}.`);
                    return;
                }
               
                const shareCertificateData = JSON.parse(JSON.stringify(shareCertificateLocal));
                
                if (props.dataShareCertificate.value.id) emit('update', shareCertificateData.value);
            };

            

            return {
                shareCertificateLocal, 
                
                // UI
                onSubmit,
                resetShareCertificateLocal,
            };
        };
         
        const {
            shareCertificateLocal,

            // UI
            onSubmit, 
            resetShareCertificateLocal
        } = useShareCertificateHandler(toRefs(props), emit);

        const { refFormObserver, getValidationState, resetForm, clearForm } = formValidation(shareCertificateLocal);

        function clearFormData() {
            resetShareCertificateLocal();
            clearForm();
        };

        return {
            // Add New
            onSubmit,
            shareCertificateLocal,

            // Form Validation
            resetForm,
            clearForm,
            refFormObserver,
            getValidationState, 
            
            // Methods
            clearFormData,
        };
    },
};
</script>

<style scope>
.share-certificate-split .preloader {
    width: 15px;
    height: 15px;
}

.share-certificate-split .preloader div:before {
    background: var(--primary-color);
}

.share-certificate-split .action-button .preloader div:before {
    background: white;
}

.side-bar-header{
   background: #f6f6f6 !important;
 } 
 
/* select disabled color overwrite */
.vs--disabled .vs__dropdown-toggle {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__search {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__open-indicator {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__selected{
    cursor: context-menu!important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/quill.scss";
</style>